import { ReactFlow, useNodesState, useEdgesState, addEdge, Edge, NodeChange } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useState } from "react";
import { Handle, Position } from '@xyflow/react';
import { Box, Grid2, IconButton, TextField, Typography } from "@mui/material";
import { Vec2 } from "../../../models/SyntheticMap";
import { Delete, Edit } from "@mui/icons-material";
import { showInputDialog } from "../../../components/UniversalDialog";
import { scenarioCleanup } from "./ScenarioClient";
import { Scenario, ScenarioBlock } from "../../../models/Scenario";
import { BaseAgent } from "../agents/BaseAgent";


function ScenarioBlockNodeView(props: any) {
  const block = props.data.block as ScenarioBlock;
  const [text, setText] = useState(block.text);
  return (
    <div style={{
      padding: '1px',
      minWidth: '250px',
      border: '1px solid #888',
    }} >
      <Grid2 container direction="row" sx={{ backgroundColor: '#000050' }} justifyContent="space-between" >
        <Grid2 flexGrow={1} sx={{ textAlign: 'left', paddingLeft: '16px', paddingTop: '8px', paddingRight: '32px', paddingBottom: '8px' }}>
          <Typography variant="h6">{block.id}</Typography>
        </Grid2>
        <Grid2 flexGrow={0} sx={{ textAlign: 'right', paddingTop: '7px' }}>
          <IconButton
            onClick={async () => {
              const oldId = block.id;
              const newId = await showInputDialog('Enter new block ID', 'New Block ID', block.id);
              if (!newId || newId.trim() === '') {
                return;
              }
              block.id = newId.trim();
              props.data.label = newId.trim();
              for (const block of props.data.scenario.blocks) {
                for (const output of block.outputs) {
                  if (output.blockId === oldId) {
                    output.blockId = newId.trim();
                  }
                }
              }
            }}
            size="small"
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => {
              const scenario = props.data.scenario as Scenario;
              scenario.blocks = scenario.blocks.filter(b => b !== block);
              scenarioCleanup(scenario);
              props.data.setScenario({ ...scenario });
            }}
            size="small"
          >
            <Delete />
          </IconButton>
        </Grid2>
      </Grid2>
      <TextField
        value={text}
        onChange={(e) => {
          block.text = e.target.value;
          setText(e.target.value);
        }}
        variant='outlined'
        size="small"
        multiline
        rows={4}
        fullWidth
        placeholder="Block text"
      />
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
    </div>
  );
}


export interface ScenarioProps {
  agent: BaseAgent;
  scenario: Scenario;
  setScenario: (newScenario: Scenario) => void;
}


const initialEdges: Edge[] = [
  { id: 'e1-2', source: '1', target: '2' },
];

export interface ScenarioBlockNode {
  id: string;
  type: 'scenarioBlockNode';
  position: { x: number; y: number };
  data: {
    label: string;
    block: ScenarioBlock;
    scenario: Scenario;
    setScenario: (newScenario: Scenario) => void;
  };
}

export function AgentBlocks({ agent, scenario, setScenario }: ScenarioProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<ScenarioBlockNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  useEffect(() => {
    setEdges([]);
    setNodes([]);
    setTimeout(() => {
      if (!agent || !scenario) {
        return;
      }
      const actorBlocks = scenario.blocks.filter(b => b.actorId === agent.visual._id);
      const newNodes: ScenarioBlockNode[] = [];
      const newEdges: Edge[] = [];
      for (const block of actorBlocks) {
        newNodes.push({
          id: block.id,
          type: 'scenarioBlockNode',
          position: block.position,
          data: {
            label: block.id,
            block,
            scenario,
            setScenario
          },
        });
        for (const output of block.outputs) {
          const targetBlock = scenario.blocks.find(b => b.id === output.blockId);
          if (targetBlock) {
            newEdges.push({
              id: `e-${block.id}-${targetBlock.id}`,
              source: block.id,
              target: targetBlock.id,
              label: output.condition,
            });
          }
        }
      }
      setNodes(newNodes);
      setEdges(newEdges);
      
    }, 500);

  }, [agent, scenario]);
  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges],
  );
  const handleNodesChange = (changes: NodeChange<ScenarioBlockNode>[]) => {
    changes.forEach((change) => {
      if (change.type === 'position' && change.position && change.id) {
        const updatedNode = nodes.find(node => node.id === change.id);
        if (!updatedNode) {
          return;
        }
        updatedNode.data.block.position = change.position;
        // console.log(`Node ${change.id} moved to: x=${change.position.x}, y=${change.position.y}`);
        // Update the position of the node in the nodes state
        // change.
        // // Log new position (or update data source, etc.)
        // console.log(`Node ${change.id} moved to: x=${change.position.x}, y=${change.position.y}`);
      }
    });
    // Apply changes to nodes state
    onNodesChange(changes);
  };

  const onEdgeDoubleClick =
    (event: any, edge: Edge) => {
      // Log edge details
      // console.log(`Double-clicked edge: ID=${edge.id}, Source=${edge.source}, Target=${edge.target}, Label=${edge.label}`);
      const block = nodes.find(n => n.id === edge.source)?.data.block;
      if (!block) {
        console.error(`Block with ID ${edge.source} not found.`);
        return;
      }
      const output =
        block.outputs.find(o => o.blockId === edge.target) ||
        block.outputs.find(o => o.condition === edge.label);
      if (!output) {
        console.error(`Output for edge ${edge.id} not found in block ${block.id}.`);
        return;
      }
      showInputDialog('Edit Condition', 'Condition for this edge', output.condition)
        .then((newCondition) => {
          if (newCondition) {
            if (newCondition === 'delete') {
              block.outputs = block.outputs.filter(o => o !== output);
            } else {
              output.condition = newCondition;
            }
            setScenario({ ...scenario });
          }
        })
        .catch((error) => {
          console.error('Error editing condition:', error);
        });
    };

  const screenToFlowPosition = (screenPosition: Vec2) => {
    const div = document.getElementById('flowContainer');
    if (!div) {
      console.error('Flow container not found');
      return screenPosition;
    }
    const rect = div.getBoundingClientRect();
    return {
      x: screenPosition.x - rect.left,
      y: screenPosition.y - rect.top,
    };
  };

  if (!agent) {
    return null;
  }

  return (
    <div id='flowContainer' style={{
      margin: 0,
      padding: 0,
      width: '100%',
      height: '100%',
    }}>
      <ReactFlow
        nodeTypes={{ scenarioBlockNode: ScenarioBlockNodeView }}
        colorMode="dark"
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeDoubleClick={onEdgeDoubleClick}
        onConnectEnd={(event, connectionState) => {
          if (connectionState.isValid && connectionState.fromNode && connectionState.toNode) {
            const sourceBlock = nodes.find(n => n.id === connectionState.fromNode!.id);
            const targetBlock = nodes.find(n => n.id === connectionState.toNode!.id);
            if (!sourceBlock || !targetBlock) {
              console.error('Source or target block not found for connection');
              return;
            }
            sourceBlock.data.block.outputs.push({
              condition: '',
              blockId: targetBlock.id
            });
            setScenario({ ...scenario });
          }
          if (!connectionState.isValid && connectionState.fromNode) {
            const sourceBlock = nodes.find(n => n.id === connectionState.fromNode!.id);
            const { clientX, clientY } =
              'changedTouches' in event ? event.changedTouches[0] : event;
            const newBlock = {
              id: `Next`,
              actorId: agent.visual._id,
              type: 'default',
              text: '',
              isBuilt: false,
              outputs: [],
              position: screenToFlowPosition({
                x: clientX,
                y: clientY,
              }),
            }
            sourceBlock!.data.block.outputs.push({
              condition: '',
              blockId: newBlock.id
            });
            scenario.blocks.push(newBlock);
            setScenario({ ...scenario });
          }
        }}
      />
    </div>
  );
}